import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineDot,
} from '@mui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { CircularProgress, useTheme, TextField, Box } from '@mui/material';
import {
  SelectAutocomplete,
  SelectAutocompleteOption,
  toSelectAutocompleteOptions,
} from '@eng-portal/czi-extensions-react';
import { Chip, Grid, Switch, Typography, Button, Tooltip } from '@material-ui/core';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { argusApiRef } from '@eng-portal/plugin-argus-common/src/argusApiRef';
import { ArgusEventCard } from '../Cards';
import { ArgusEvent } from '@eng-portal/plugin-argus-common/src/argusApi';
import { DateRangePicker, DateRange, getDateRangeFromRelativeKey } from '../DateRangePicker';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

type ArgusEventsTimelineProps = {
  appName?: string
  envName?: string
  clusterName?: string
  stackName?: string
  eventTypes?: string[]
};

// Helper to parse date range from URL params
function parseDateRangeFromParams(searchParams: URLSearchParams): DateRange | null {
  // Check for relative range first (takes precedence)
  const relativeKey = searchParams.get('relativeRange');
  if (relativeKey) {
    return getDateRangeFromRelativeKey(relativeKey);
  }

  // Fall back to absolute timestamps
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const labelParam = searchParams.get('dateLabel');

  if (fromParam && toParam) {
    const from = dayjs(fromParam);
    const to = dayjs(toParam);
    if (from.isValid() && to.isValid()) {
      return { from, to, label: labelParam || 'Custom', relativeKey: undefined };
    }
  }
  return null;
}

export function ArgusEventsTimeline(props: ArgusEventsTimelineProps): JSX.Element {
  const { appName: initialAppName, envName: initialEnvName, clusterName: initialClusterName, stackName, eventTypes } = props;
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params, falling back to props or defaults
  const getInitialEventTypes = (): string[] => {
    const urlEventTypes = searchParams.get('eventTypes');
    if (urlEventTypes) return urlEventTypes.split(',').filter(Boolean);
    return eventTypes || [];
  };

  const getInitialSourceSystems = (): string[] => {
    const urlSourceSystems = searchParams.get('sourceSystems');
    if (urlSourceSystems) return urlSourceSystems.split(',').filter(Boolean);
    return [];
  };

  const getInitialAppName = (): string | undefined => {
    return searchParams.get('app') || initialAppName;
  };

  const getInitialEnvName = (): string | undefined => {
    return searchParams.get('env') || initialEnvName;
  };

  const getInitialClusterName = (): string | undefined => {
    return searchParams.get('cluster') || initialClusterName;
  };

  const getInitialStackName = (): string | undefined => {
    return searchParams.get('stack') || stackName;
  };

  const getInitialUseUtc = (): boolean => {
    return searchParams.get('utc') === 'true';
  };

  const getInitialSearchTerms = (): string[] => {
    const urlTerms = searchParams.get('terms');
    if (urlTerms) return urlTerms.split(',').filter(Boolean);
    return [];
  };

  const getInitialDateRange = (): DateRange | null => {
    const fromUrl = parseDateRangeFromParams(searchParams);
    if (fromUrl) return fromUrl;
    // Default to "Last 24 hours" (relative)
    return {
      from: dayjs().subtract(24, 'hours'),
      to: dayjs(),
      label: 'Last 24 hours',
      relativeKey: 'last_24_hours',
    };
  };

  const [eventTypeOptions, setEventTypeOptions] = useState<SelectAutocompleteOption[]>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(getInitialEventTypes);
  const [sourceSystemOptions, setSourceSystemOptions] = useState<SelectAutocompleteOption[]>([]);
  const [selectedSourceSystems, setSelectedSourceSystems] = useState<string[]>(getInitialSourceSystems);
  const [useUtc, setUseUtc] = useState<boolean>(getInitialUseUtc);
  const [appName, setAppName] = useState<string|undefined>(getInitialAppName);
  const [appNameOptions, setAppNameOptions] = useState<SelectAutocompleteOption[]>([]);
  const [envName, setEnvName] = useState<string|undefined>(getInitialEnvName);
  const [envNameOptions, setEnvNameOptions] = useState<SelectAutocompleteOption[]>([]);
  const [appEnvMapping, setAppEnvMapping] = useState<Record<string, string[]>>({});
  const [clusterName, setClusterName] = useState<string|undefined>(getInitialClusterName);
  const [clusterNameOptions, setClusterNameOptions] = useState<SelectAutocompleteOption[]>([]);
  const [stackNameFilter, setStackNameFilter] = useState<string|undefined>(getInitialStackName);
  const [stackNameOptions, setStackNameOptions] = useState<SelectAutocompleteOption[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | null>(getInitialDateRange);
  const [searchTerms, setSearchTerms] = useState<string[]>(getInitialSearchTerms);
  const [searchTermInput, setSearchTermInput] = useState<string>('');

  // Pagination state
  const [events, setEvents] = useState<ArgusEvent[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalEventCount, setTotalEventCount] = useState<number>(0);

  const argusApi = useApi(argusApiRef);

  // Sync filter state to URL query params
  useEffect(() => {
    const params = new URLSearchParams();

    if (selectedEventTypes.length > 0) {
      params.set('eventTypes', selectedEventTypes.join(','));
    }
    if (selectedSourceSystems.length > 0) {
      params.set('sourceSystems', selectedSourceSystems.join(','));
    }
    if (appName) {
      params.set('app', appName);
    }
    if (envName) {
      params.set('env', envName);
    }
    if (clusterName) {
      params.set('cluster', clusterName);
    }
    if (stackNameFilter) {
      params.set('stack', stackNameFilter);
    }
    if (useUtc) {
      params.set('utc', 'true');
    }
    if (dateRange) {
      // If it's a relative range, store the key; otherwise store absolute timestamps
      if (dateRange.relativeKey) {
        params.set('relativeRange', dateRange.relativeKey);
      } else {
        params.set('from', dateRange.from.toISOString());
        params.set('to', dateRange.to.toISOString());
        if (dateRange.label) {
          params.set('dateLabel', dateRange.label);
        }
      }
    }
    if (searchTerms.length > 0) {
      params.set('terms', searchTerms.join(','));
    }

    setSearchParams(params, { replace: true });
  }, [
    selectedEventTypes, selectedSourceSystems, appName, envName,
    clusterName, stackNameFilter, useUtc, dateRange, searchTerms,
    setSearchParams,
  ]);

  // Load event types on mount
  useEffect(() => {
    const loadEventTypes = async (): Promise<void> => {
      try {
        const eventTypesResponse = await argusApi.listEventTypes();
        setEventTypeOptions(toSelectAutocompleteOptions(eventTypesResponse));
      } catch (err) {
        console.error('Failed to load event types:', err);
      }
    };
    loadEventTypes();
  }, [argusApi]);

  // Load source systems on mount
  useEffect(() => {
    const loadSourceSystems = async (): Promise<void> => {
      try {
        const sourceSystemsResponse = await argusApi.listSourceSystems();
        setSourceSystemOptions(toSelectAutocompleteOptions(sourceSystemsResponse));
      } catch (err) {
        console.error('Failed to load source systems:', err);
      }
    };
    loadSourceSystems();
  }, [argusApi]);

  // Load app/env/cluster names on mount
  useEffect(() => {
    const loadAppNames = async (): Promise<void> => {
      try {
        const appNamesResponse = await argusApi.listApps();
        setAppNameOptions(toSelectAutocompleteOptions(appNamesResponse.map(app => app.name)));

        const updatedAppEnvMapping: Record<string, string[]> = {};
        for (const app of appNamesResponse) {
          for (const env of app.envs || []) {
            const envs = updatedAppEnvMapping[app.name] ? [...updatedAppEnvMapping[app.name], env.name] : [env.name];
            updatedAppEnvMapping[app.name] = envs;
          }
        }
        setAppEnvMapping(updatedAppEnvMapping);
        if (initialAppName) {
          // setAppName(initialAppName);
          setEnvNameOptions(toSelectAutocompleteOptions(updatedAppEnvMapping[initialAppName] ? updatedAppEnvMapping[initialAppName] : []));
        }
      } catch (err) {
        console.error('Failed to load app names:', err);
      }
    };

    const loadClusterNames = async (): Promise<void> => {
      try {
        const clusterNamesResponse = await argusApi.listClusters();
        setClusterNameOptions(toSelectAutocompleteOptions(clusterNamesResponse.map(cluster => cluster.name)));
      } catch (err) {
        console.error('Failed to load cluster names:', err);
      }
    };

    // if (!initialAppName) {
      loadAppNames();
    // }
    if (!initialClusterName) {
      loadClusterNames();
    }
  }, [argusApi]);

  // Load stack names when app and env are selected
  useEffect(() => {
    const loadStackNames = async (): Promise<void> => {
      if (!appName || !envName) {
        setStackNameOptions([]);
        return;
      }
      try {
        const stacksResponse = await argusApi.listStacks(appName, envName);
        setStackNameOptions(toSelectAutocompleteOptions(stacksResponse.map(stack => stack.name)));
      } catch (err) {
        console.error('Failed to load stack names:', err);
        setStackNameOptions([]);
      }
    };
    loadStackNames();
  }, [argusApi, appName, envName]);

  // Effective stack name: use prop if provided, otherwise use filter
  const effectiveStackName = stackName || stackNameFilter;

  // Load events function
  const loadEvents = useCallback(async (page: number, resetEvents: boolean = false): Promise<void> => {
    if (resetEvents) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await argusApi.listEvents({
        event_type: selectedEventTypes,
        source_system: selectedSourceSystems.length > 0 ? selectedSourceSystems : undefined,
        app_name: appName,
        env_name: envName,
        cluster_name: clusterName,
        stack_name: effectiveStackName,
        start_time: dateRange ? dateRange.from.toISOString() : undefined,
        end_time: dateRange ? dateRange.to.toISOString() : undefined,
        page,
        terms: searchTerms.length > 0 ? searchTerms : undefined,
      });

      setEvents(prev => resetEvents ? response.events : [...prev, ...response.events]);
      setHasMore(response.hasMore);
      setCurrentPage(page);
      setTotalEventCount(response.totalEventCount);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load events'));
      setHasMore(false); // Stop infinite scroll retries on error
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [argusApi, selectedEventTypes, selectedSourceSystems, appName, envName, clusterName, effectiveStackName, dateRange, searchTerms]);

  // Load more events
  const loadMoreEvents = useCallback((): void => {
    if (!loadingMore && hasMore) {
      loadEvents(currentPage + 1, false);
    }
  }, [loadEvents, currentPage, hasMore, loadingMore]);

  // Load first page when filters change
  useEffect((): void => {
    const loadFirstPage = async (): Promise<void> => {
      setEvents([]);
      setCurrentPage(1);
      setHasMore(true);
      setLoading(true);
      setError(null);

      try {
        const response = await argusApi.listEvents({
          event_type: selectedEventTypes,
          source_system: selectedSourceSystems.length > 0 ? selectedSourceSystems : undefined,
          app_name: appName,
          env_name: envName,
          cluster_name: clusterName,
          stack_name: effectiveStackName,
          start_time: dateRange ? dateRange.from.toISOString() : undefined,
          end_time: dateRange ? dateRange.to.toISOString() : undefined,
          page: 1,
          terms: searchTerms.length > 0 ? searchTerms : undefined,
        });

        setEvents(response.events);
        setHasMore(response.hasMore);
        setCurrentPage(1);
        setTotalEventCount(response.totalEventCount);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load events'));
        setHasMore(false); // Stop infinite scroll retries on error
      } finally {
        setLoading(false);
      }
    };

    loadFirstPage();
  }, [argusApi, selectedEventTypes, selectedSourceSystems, appName, envName, clusterName, effectiveStackName, dateRange, searchTerms]);

  const scrollboxRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Use refs to track loading states to avoid recreating the observer constantly
  const isLoadingRef = useRef(loading || loadingMore);

  // Keep ref in sync with loading states
  useEffect(() => {
    isLoadingRef.current = loading || loadingMore;
  }, [loading, loadingMore]);

  // Intersection observer for infinite scroll
  useEffect((): any => {
    const scrollContainer = scrollboxRef.current;
    const trigger = loadMoreTriggerRef.current;

    if (!scrollContainer || !trigger) return;

    const observer = new IntersectionObserver(
      ([entry]): void => {
        // Check the ref instead of state to avoid recreating observer
        if (entry.isIntersecting && hasMore && !isLoadingRef.current && !error) {
          loadMoreEvents();
        }
      },
      {
        root: scrollContainer, // Use the scrollable container as root
        rootMargin: '100px', // Trigger 100px before the element comes into view
      }
    );

    observer.observe(trigger);

    return (): void => {
      observer.unobserve(trigger);
    };
  }, [hasMore, loadMoreEvents, error]);

  const handleSelectedEventTypesChange = (selected: SelectAutocompleteOption[]): void => {
    setSelectedEventTypes((selected || []).map(option => option?.value).filter(Boolean) as string[]);
  };

  const handleSelectedSourceSystemsChange = (selected: SelectAutocompleteOption[]): void => {
    setSelectedSourceSystems((selected || []).map(option => option?.value).filter(Boolean) as string[]);
  };

  const handleClusterNameChange = (selected: SelectAutocompleteOption[]): void => {
    setClusterName((selected || [])[0]?.value);
  };

  const handleAppNameChange = (selected: SelectAutocompleteOption[]): void => {
    const updatedAppName = (selected || [])[0]?.value;
    setAppName(updatedAppName);
    if (updatedAppName !== appName) {
      setEnvName(undefined);
      setStackNameFilter(undefined);
    }
    setEnvNameOptions(toSelectAutocompleteOptions(updatedAppName && appEnvMapping[updatedAppName] ? appEnvMapping[updatedAppName] : []));
  };

  const handleEnvNameChange = (selected: SelectAutocompleteOption[]): void => {
    const updatedEnvName = (selected || [])[0]?.value;
    setEnvName(updatedEnvName);
    if (updatedEnvName !== envName) {
      setStackNameFilter(undefined);
    }
  };

  const handleStackNameChange = (selected: SelectAutocompleteOption[]): void => {
    setStackNameFilter((selected || [])[0]?.value);
  };

  // Sort envs with rdev first, then staging, then prod, then anything else
  const sortEnvItems = (a: SelectAutocompleteOption, b: SelectAutocompleteOption): number => {
    const order = ['rdev', 'staging', 'prod'];
    const aIndex = order.indexOf(a.value);
    const bIndex = order.indexOf(b.value);

    // If both items are in the order array, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one item is in the order array, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither item is in the order array, sort alphabetically
    return a.value.localeCompare(b.value);
  };

  const showFilter: Record<string, boolean> = {
    app: !initialAppName,
    cluster: !initialClusterName && !initialAppName,
    env: true,
    stack: !stackName, // Show stack filter unless a fixed stackName prop is provided
    dateRange: true,
  };

  const handleAddSearchTerm = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && searchTermInput.trim()) {
      event.preventDefault();
      const newTerm = searchTermInput.trim();
      if (!searchTerms.includes(newTerm)) {
        setSearchTerms([...searchTerms, newTerm]);
      }
      setSearchTermInput('');
    }
  };

  const handleRemoveSearchTerm = (termToRemove: string): void => {
    setSearchTerms(searchTerms.filter(term => term !== termToRemove));
  };

  const handleLockDateRange = (): void => {
    if (dateRange && dateRange.relativeKey) {
      // Convert relative range to absolute by removing the relativeKey
      setDateRange({
        from: dateRange.from,
        to: dateRange.to,
        label: dateRange.label,
        relativeKey: undefined, // This makes it absolute
      });
    }
  };

  const controlPanelContent = (
    <Grid container direction="column" spacing={1}>
      <Grid item xs={12} style={{ marginBottom: '16px' }}>
        <TextField
          fullWidth
          label="Search Terms"
          placeholder="Type a search term and press Enter"
          value={searchTermInput}
          onChange={(e) => setSearchTermInput(e.target.value)}
          onKeyDown={handleAddSearchTerm}
          helperText="Press Enter to add each search term"
        />
        {searchTerms.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, alignItems: 'center' }}>
            {searchTerms.map((term) => (
              <Chip
                key={term}
                label={term}
                size="small"
                onDelete={() => handleRemoveSearchTerm(term)}
              />
            ))}
            <Chip
              label="Clear all"
              size="small"
              variant="outlined"
              onClick={() => setSearchTerms([])}
              style={{ marginLeft: 8 }}
            />
          </Box>
        )}
      </Grid>
      <Grid item xs={12}>
        <SelectAutocomplete
          label="Filter by Event Type"
          items={eventTypeOptions}
          selectedItems={toSelectAutocompleteOptions(selectedEventTypes)}
          setSelectedItems={handleSelectedEventTypesChange}
          allowSelectAll={false}
          multiple
          sortItems
        />
      </Grid>
      <Grid item xs={12}>
        <SelectAutocomplete
          label="Filter by Source System"
          items={sourceSystemOptions}
          selectedItems={toSelectAutocompleteOptions(selectedSourceSystems)}
          setSelectedItems={handleSelectedSourceSystemsChange}
          allowSelectAll={false}
          multiple
          sortItems
        />
      </Grid>
      {showFilter.cluster && <Grid item xs={12}>
        <SelectAutocomplete
          label="Filter by Cluster"
          items={clusterNameOptions}
          selectedItems={toSelectAutocompleteOptions(clusterName ? [clusterName] : [])}
          setSelectedItems={handleClusterNameChange}
          allowSelectAll={false}
          sortItems
          clearable
        />
      </Grid>}
      {showFilter.app && <Grid item xs={12}>
        <SelectAutocomplete
          label="Filter by App"
          items={appNameOptions}
          selectedItems={toSelectAutocompleteOptions(appName ? [appName] : [])}
          setSelectedItems={handleAppNameChange}
          allowSelectAll={false}
          sortItems
          clearable
        />
      </Grid>}
      {showFilter.env && <Grid item xs={12}>
        <SelectAutocomplete
          label={appName ? "Filter by Env" : "Filter by Env (Select App first)"}
          items={envNameOptions}
          selectedItems={toSelectAutocompleteOptions(envName ? [envName] : [])}
          setSelectedItems={handleEnvNameChange}
          allowSelectAll={false}
          sortItems
          sortItemsFn={sortEnvItems}
          disabled={!appName}
          clearable
        />
      </Grid>}
      {showFilter.stack && <Grid item xs={12}>
        <SelectAutocomplete
          label={
            appName && envName
              ? "Filter by Stack"
              : appName
                ? "Filter by Stack (Select Env first)"
                : "Filter by Stack (Select App & Env first)"
          }
          items={stackNameOptions}
          selectedItems={toSelectAutocompleteOptions(stackNameFilter ? [stackNameFilter] : [])}
          setSelectedItems={handleStackNameChange}
          allowSelectAll={false}
          sortItems
          disabled={!appName || !envName}
          clearable
        />
      </Grid>}
      {showFilter.dateRange && <Grid item xs={12}>
        <Box display="flex" alignItems="flex-start">
          <Box flex={dateRange?.relativeKey ? 1 : undefined} width={!dateRange?.relativeKey ? '100%' : undefined}>
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
            />
          </Box>
          {dateRange?.relativeKey && (
            <Box ml={1}>
              <Tooltip title="Lock this time range for sharing (converts to absolute timestamps)">
                <Button
                  variant="outlined"
                  onClick={handleLockDateRange}
                  style={{
                    minHeight: 56,
                    height: 56,
                    minWidth: 56,
                    padding: '8px'
                  }}
                >
                  <LockOpenIcon />
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Grid>}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center">
          <Typography variant="body1" component="span">
            UTC Timestamps
          </Typography>
          <Switch
            checked={useUtc}
            onChange={(_, checked) => setUseUtc(checked)}
            name="UTC Timestamps"
          />
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Box display="flex" style={{ height: '95vh' }}>
      {/* Sidebar control panel */}
      <Box
        style={{
          width: 350,
          minWidth: 350,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box style={{ padding: 16, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', flexShrink: 0 }}>
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Box style={{ overflow: 'auto', flex: 1, padding: 16 }}>
          {controlPanelContent}
        </Box>
      </Box>

      {/* Events area with header */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', marginLeft: '16px' }}>
        {/* Header with event counts */}
        <Box
          style={{
            padding: '16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '24px',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Showing {events.length.toLocaleString()} of {totalEventCount.toLocaleString()} matching events
          </Typography>
        </Box>

        {/* Scrollable events area */}
        <Box style={{ flex: 1, overflow: 'auto', padding: '16px' }} ref={scrollboxRef}>
          {error && <Typography color="error">Error loading events: {error.message}</Typography>}
          {!error && !loading && events.length === 0 && (
            <Typography>No events found for the selected filters. Try expanding the date range.</Typography>
          )}
          {/* Loading indicator for initial load */}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          )}
          <Timeline sx={{ alignItems: 'flex-start' }}>
            {events.map(event => (
              <TimelineItem key={event.id} sx={{ minWidth: '100%', maxWidth: '100%' }}>
                <TimelineOppositeContent sx={{ maxWidth: '15%' }}>
                  {useUtc
                    ? new Date(event.occurred_at).toISOString()
                    : new Date(event.occurred_at).toLocaleString()}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <ArgusEventCard event={event} useUtc={useUtc} />
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
          {/* Invisible trigger element for infinite scroll */}
          {hasMore && <div ref={loadMoreTriggerRef} style={{ height: '1px' }} />}
          {/* Loading indicator for additional pages */}
          {loadingMore && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
}
